import { Injectable } from '@angular/core';
import { AppConstants } from '../constants';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'environments/environment';
import { UtilsService } from './utils.service';
import { DataService } from './data.service';
import { EntLoaderService } from '@ng/ent-components/ent-loader';

@Injectable({
  providedIn: 'root'
})
export class CommonService {
  siteKey: string;
  window: any;
  captchaV3Token = new BehaviorSubject<string>('');

  constructor(
    private lfgLoaderService: EntLoaderService,
    public dataService: DataService) {
    const envDetails = this.getEnvDetails();
    if (envDetails && envDetails.deploy === 'prod') {
      this.siteKey = AppConstants.GOOGLEV3CAPTCHA.PROD_SITE_KEY;
    } else if (envDetails && (envDetails.deploy === 'nonprod' || envDetails.deploy === 'test')) {
      this.siteKey = AppConstants.GOOGLEV3CAPTCHA.SITE_KEY;
    } else if (environment.environ === '') {
      this.siteKey = AppConstants.GOOGLEV3CAPTCHA.PROD_SITE_KEY;
    } else {
      this.siteKey = AppConstants.GOOGLEV3CAPTCHA.SITE_KEY;
    }
  }

  private getEnvDetails() {
    const hostName = window.location.hostname;

    if (hostName.indexOf('mgeinterview.web.lfg.com') !== -1) {
      return { deploy: 'prod' };
    }

    if (hostName.indexOf('mgeinterview-dev.web.lfg.com') !== -1) {
      return { deploy: 'nonprod' };
    }

    if (hostName.indexOf('mgeinterview-test.web.lfg.com') !== -1) {
      return { deploy: 'test' };
    }
    if (hostName.indexOf('api-mg.einterview-ext-test.aws.cloud.lfg.com') !== -1) {
      return { deploy: 'test' };
    }

    return null;
  }

  generateGoogleV3Captcha() {
    this.loadCaptchaScript();
    this.getCaptchaToken().subscribe((token) => {
      this.setCaptchaV3Token(token);
    });
  }

  /** 
   * This function is used to create and inject the captcha element in the DOM
   */
  loadCaptchaScript(): void {
    if (!document.getElementById(AppConstants.GOOGLEV3CAPTCHA.RECAPTCHA_ID)) {
      const captchaEle = document.createElement('script');
      captchaEle.id = AppConstants.GOOGLEV3CAPTCHA.RECAPTCHA_ID;
      captchaEle.src = AppConstants.GOOGLEV3CAPTCHA.RECAPTCHA_RENDER_URL + this.siteKey;
      captchaEle.async = true;
      captchaEle.defer = true;
      this.dataService.setData('siteKey', this.siteKey);
      this.dataService.setData('captchaEnabled', AppConstants.GOOGLEV3CAPTCHA.captchaEnabled);
      document.body.appendChild(captchaEle);
    }
  }

  /**
   * This function is used to fetch the captcha response
   *  @returns captcha token
   */
  getCaptchaToken(): Observable<string> {
    this.loadCaptchaScript();
    return new Observable((subscriber) => {
      const maxAttempts = 10;
      let attempts = 0;

      const executeCaptcha = () => {
        const recaptcha = (window as any).grecaptcha?.enterprise;
        if (!this.siteKey || !recaptcha || typeof recaptcha.ready !== 'function' || typeof recaptcha.execute !== 'function') {
          if (attempts < maxAttempts) {
            attempts++;
            setTimeout(executeCaptcha, 300);
            return;
          }
          subscriber.error('reCAPTCHA not loaded');
          return;
        }

        recaptcha.ready(() => {
          recaptcha
            .execute(this.siteKey, { action: 'submit' })
            .then((token: string) => {
              if (token) {
                subscriber.next(token);
                subscriber.complete();
                return;
              }
              subscriber.error('Captcha token generation failed');
            })
            .catch(() => {
              if (attempts < maxAttempts) {
                attempts++;
                setTimeout(executeCaptcha, 300);
                return;
              }
              subscriber.error('Captcha token generation failed');
            });
        });
      };

      setTimeout(executeCaptcha, 200);
    });
  }

  /**
   * Once token value is generated, holds it across the application
   */
  getCaptchaV3Token = this.captchaV3Token.asObservable();
  setCaptchaV3Token(token) {
    this.captchaV3Token.next(token);
  }

  /**
   * Removes special characters in GUID if any
   * Formats GUID into 8-4-4-4-12 character pattern
   * @returns formatted GUID
   */
  guidFormatter(guid) {
    const isSpecialChar = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/;
    if (isSpecialChar.test(guid)) {
      guid = guid.replace(/[^a-zA-Z0-9]/g, "");
    }
    guid = guid.replace(/(.{8})(.{4})(.{4})(.{4})(.{12})/, "$1-$2-$3-$4-$5");
    return guid;
  }

  /**
   * To mask value
   */
  mask(value, lastFewToShow = 4, maskSymbol = '*') {
    return ('' + value).slice(0, -lastFewToShow).replace(/./g, maskSymbol) + ('' + value).slice(-lastFewToShow);
  }

  /**
   * Return value if present, else return '' or null
   */
  isValuePresent(value, emptyValue) {
    if (value)
      return value;
    else
      return emptyValue;
  }
}
